import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import { User, Mail, Phone, Shield, Save, Key, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const roleLabels = {
  super_admin: "Super Administrador",
  admin_conferencia: "Admin Conferencia Episcopal",
  admin_diocesano: "Admin Diocesano",
  admin_vicaria: "Admin Vicaría",
  parroco: "Párroco",
  vicario_parroquial: "Vicario Parroquial",
  secretaria: "Secretaria",
  lector: "Solo Lectura",
};

const roleColors = {
  super_admin: "bg-purple-100 text-purple-800 border-purple-200",
  admin_conferencia: "bg-blue-100 text-blue-800 border-blue-200",
  admin_diocesano: "bg-indigo-100 text-indigo-800 border-indigo-200",
  admin_vicaria: "bg-cyan-100 text-cyan-800 border-cyan-200",
  parroco: "bg-emerald-100 text-emerald-800 border-emerald-200",
  vicario_parroquial: "bg-teal-100 text-teal-800 border-teal-200",
  secretaria: "bg-amber-100 text-amber-800 border-amber-200",
  lector: "bg-stone-100 text-stone-800 border-stone-200",
};

export default function PerfilPage() {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    if (user) {
      setFormData({
        nombre: user.nombre || "",
        apellido: user.apellido || "",
        email: user.email || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put(`/users/${user.id}`, {
        nombre: formData.nombre,
        apellido: formData.apellido,
      });
      
      // Update local storage and context
      const updatedUser = { ...user, ...formData };
      localStorage.setItem("ecclesia_user", JSON.stringify(updatedUser));
      login(updatedUser, localStorage.getItem("ecclesia_token"));
      
      toast.success("Perfil actualizado correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar el perfil");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);

    try {
      await api.put(`/users/${user.id}/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      
      toast.success("Contraseña actualizada correctamente");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="perfil-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
          <User className="w-8 h-8 text-rose-700" strokeWidth={1.5} />
          Mi Perfil
        </h1>
        <p className="text-stone-600 mt-1">Administra tu información personal</p>
      </div>

      {/* Profile Info Card */}
      <Card className="border-stone-200 bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-medium text-stone-800">
              Información Personal
            </CardTitle>
            <Badge variant="outline" className={roleColors[user?.rol] || roleColors.lector}>
              <Shield className="w-3 h-3 mr-1" />
              {roleLabels[user?.rol] || user?.rol}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="h-11"
                  data-testid="perfil-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apellido">Apellido</Label>
                <Input
                  id="apellido"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleChange}
                  className="h-11"
                  data-testid="perfil-apellido-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="h-11 pl-10 bg-stone-50"
                />
              </div>
              <p className="text-xs text-stone-500">El correo electrónico no se puede modificar</p>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading}
                className="bg-rose-950 hover:bg-rose-900 text-white"
                data-testid="guardar-perfil-btn"
              >
                {loading ? (
                  <span className="animate-pulse">Guardando...</span>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card className="border-stone-200 bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Key className="w-5 h-5 text-stone-600" />
              Seguridad
            </CardTitle>
            {!showPasswordSection && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordSection(true)}
                data-testid="cambiar-password-btn"
              >
                Cambiar Contraseña
              </Button>
            )}
          </div>
        </CardHeader>
        {showPasswordSection && (
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Contraseña Actual</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    name="current_password"
                    type={showPassword ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="new_password">Nueva Contraseña</Label>
                <Input
                  id="new_password"
                  name="new_password"
                  type={showPassword ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  className="h-11"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmar Nueva Contraseña</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showPassword ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  className="h-11"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordSection(false);
                    setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-rose-950 hover:bg-rose-900 text-white"
                >
                  {loading ? "Actualizando..." : "Actualizar Contraseña"}
                </Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Account Info */}
      <Card className="border-stone-200 bg-stone-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-stone-200">
              <Shield className="w-5 h-5 text-stone-600" />
            </div>
            <div>
              <h3 className="font-medium text-stone-800">Información de la Cuenta</h3>
              <p className="text-sm text-stone-600 mt-1">
                Tu rol actual es <strong>{roleLabels[user?.rol] || user?.rol}</strong>. 
                {user?.entidad_id 
                  ? " Estás asignado a una entidad específica."
                  : " No estás asignado a ninguna entidad específica."}
              </p>
              <p className="text-xs text-stone-500 mt-2">
                Para cambios de rol o asignación de entidad, contacta al administrador del sistema.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
